import { useState } from 'react'
import type { CaseEndpoint, CreateRelationshipRequest } from '../../../../types/case-container'

interface UseCreateRelationshipModalArgs {
  sourceEndpoint: CaseEndpoint
  endpoints: CaseEndpoint[]
  onCreate: (data: CreateRelationshipRequest) => void
}

export interface CreateRelationshipModalState {
  formData: CreateRelationshipRequest
  setFormData: React.Dispatch<React.SetStateAction<CreateRelationshipRequest>>
  isOpenQuestion: boolean
  bidirectional: boolean
  setBidirectional: React.Dispatch<React.SetStateAction<boolean>>
  availableTargets: CaseEndpoint[]
  selectedTarget: CaseEndpoint | undefined
  handleSubmit: (e: React.FormEvent) => void
  handleStatusChange: (status: string) => void
}

export function useCreateRelationshipModal(
  args: UseCreateRelationshipModalArgs
): CreateRelationshipModalState {
  const { sourceEndpoint, endpoints, onCreate } = args
  const [formData, setFormData] = useState<CreateRelationshipRequest>({
    sourceEndpointId: sourceEndpoint.id,
    targetEndpointId: '',
    connectionType: 'network_connection',
    status: 'suspected',
    label: '',
    investigationNotes: '',
  })
  const [isOpenQuestion, setIsOpenQuestion] = useState(false)
  const [bidirectional, setBidirectional] = useState(false)

  const availableTargets = endpoints.filter((ep) => ep.id !== sourceEndpoint.id)
  const selectedTarget = endpoints.find((ep) => ep.id === formData.targetEndpointId)

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!formData.targetEndpointId) return
    const submitData: CreateRelationshipRequest = {
      ...formData,
      status: isOpenQuestion ? 'open-question' : (formData.status ?? 'suspected'),
    }
    onCreate(submitData)
    if (bidirectional) {
      const reverseData: CreateRelationshipRequest = {
        ...submitData,
        sourceEndpointId: formData.targetEndpointId,
        targetEndpointId: formData.sourceEndpointId,
      }
      onCreate(reverseData)
    }
  }

  const handleStatusChange = (status: string): void => {
    setFormData({ ...formData, status })
    setIsOpenQuestion(status === 'open-question')
  }

  return {
    formData,
    setFormData,
    isOpenQuestion,
    bidirectional,
    setBidirectional,
    availableTargets,
    selectedTarget,
    handleSubmit,
    handleStatusChange,
  }
}
